import { defineStore } from "pinia";
import { ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { getSharedEmailSetting } from "../infrastructure/adapters/sharedEmailSettingService";
/**
 * Session cache of the workspace shared email setting, read through {@link useSharedEmailSetting}.
 * A page load starts a new session, and the workspace cannot change without one (the tenant comes
 * from the token), so the cache needs no tenant key.
 */
export const useSharedEmailSettingStore = defineStore("sharedEmailSetting", () => {
    // CONSTRAINT: fails closed. `false` until a load succeeds and after a failed one: offering the
    // opt-in in a workspace that has sharing off presents a shared email as a normal outcome
    // (OP-8215). Do not default to `true`, nor keep a cached `true` over a failed revalidation.
    const enabled = ref(false);
    /** True once a first load has settled, successfully or not. */
    const settled = ref(false);
    let requestId = 0;
    /** Revalidates the setting; the last known value stays visible while the read is in flight. */
    async function load() {
        const current = ++requestId;
        try {
            const setting = await getSharedEmailSetting();
            if (current !== requestId)
                return;
            enabled.value = setting.enabled;
        }
        catch (error) {
            if (current !== requestId)
                return;
            enabled.value = false;
            logger.error("Failed to load the shared email setting", error);
        }
        settled.value = true;
    }
    /** Records a value known from elsewhere (a write, a refusal); any load in flight is dropped. */
    function remember(value) {
        requestId++;
        enabled.value = value;
        settled.value = true;
    }
    return { enabled, settled, load, remember };
});
